import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/types";
import { enrollCourse } from "@/api/enrollCourse/enrollCourse";

// Types based on data-model.md
interface ContentBlock {
  id: number;
  type: "text" | "video" | "quiz" | "file";
  content: string;
  orderNumber: number;
  metadata: Record<string, unknown> | null;
}

interface CourseModule {
  id: number;
  title: string;
  description: string;
  orderNumber: number;
  contentBlocks: ContentBlock[];
}

interface CourseDetail {
  id: number;
  title: string;
  tagline: string | null;
  description: string;
  difficultyLevel: "beginner" | "intermediate" | "advanced";
  estimatedHours: string;
  thumbnailPath: string | null;
  heroBannerPath: string | null;
  enrollmentCount: number;
  moduleCount: number;
  skills: { id: number; title: string }[];
  categories: { id: number; name: string }[];
  createdAt: string;
  updatedAt: string;
  modules: CourseModule[];
}

interface CourseEnrollment {
  id: number;
  courseId: number;
  userId: number;
  status: "enrolled" | "in_progress" | "completed" | "cancelled";
  progressPercentage: number;
  lastAccessedModuleId: number | null;
  enrolledAt: string;
  completedAt: string | null;
  cancelledAt?: string | null;
}

interface ModuleProgress {
  id: number;
  moduleId: number;
  status: "not_started" | "completed";
  completedAt: string | null;
  lastAccessedAt: string;
}

interface CourseDetailState {
  course: CourseDetail | null;
  enrollment: CourseEnrollment | null;
  moduleProgress: Record<number, ModuleProgress>;
  loading: boolean;
  error: string | null;
  enrollmentInProgress: boolean;
}

const initialState: CourseDetailState = {
  course: null,
  enrollment: null,
  moduleProgress: {},
  loading: false,
  error: null,
  enrollmentInProgress: false,
};

const courseDetailSlice = createSlice({
  name: "courseDetail",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setCourse(state, action: PayloadAction<CourseDetail | null>) {
      state.course = action.payload;
    },
    setEnrollment(state, action: PayloadAction<CourseEnrollment | null>) {
      state.enrollment = action.payload;
    },
    setModuleProgress(
      state,
      action: PayloadAction<Record<number, ModuleProgress>>,
    ) {
      state.moduleProgress = action.payload;
    },
    setEnrollmentInProgress(state, action: PayloadAction<boolean>) {
      state.enrollmentInProgress = action.payload;
    },
    updateEnrollmentOptimistic(
      state,
      action: PayloadAction<Partial<CourseEnrollment>>,
    ) {
      if (state.enrollment) {
        state.enrollment = {
          ...state.enrollment,
          ...action.payload,
        };
      }
    },
    clearCourseDetail(state) {
      state.course = null;
      state.enrollment = null;
      state.moduleProgress = {};
      state.error = null;
    },
  },
});

export default courseDetailSlice.reducer;

export const {
  setLoading,
  setError,
  setCourse,
  setEnrollment,
  setModuleProgress,
  setEnrollmentInProgress,
  updateEnrollmentOptimistic,
  clearCourseDetail,
} = courseDetailSlice.actions;

// Async thunk: Fetch course detail with enrollment and module progress
export const fetchCourseDetail =
  (courseId: number): AppThunk =>
  async (dispatch: AppDispatch) => {
    // TODO: Use courseId when API endpoint is ready
    console.log("Fetching course detail for courseId:", courseId);
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      // TODO: Replace with actual API endpoint when available
      // Expected endpoint: GET /api/learning-center/courses/:id
      // Expected response structure:
      // {
      //   course: CourseDetail,
      //   enrollment: CourseEnrollment | null,
      //   moduleProgress: ModuleProgress[]
      // }

      // Placeholder implementation
      throw new Error("fetchCourseDetail API endpoint not yet implemented");

      // Expected implementation:
      // const response = await fetchCourseDetailApi({ id: courseId });
      // dispatch(setCourse(response.course));
      // dispatch(setEnrollment(response.enrollment));
      //
      // // Convert moduleProgress array to Record<moduleId, ModuleProgress>
      // const progressMap: Record<number, ModuleProgress> = {};
      // response.moduleProgress?.forEach((progress) => {
      //   progressMap[progress.moduleId] = progress;
      // });
      // dispatch(setModuleProgress(progressMap));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch course details.";
      dispatch(setError(message));
    } finally {
      dispatch(setLoading(false));
    }
  };

// Async thunk: Enroll in course with optimistic updates
export const enrollInCourse =
  (courseId: number): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setEnrollmentInProgress(true));
    dispatch(setError(null));

    // Optimistic update: Immediately set enrollment status
    const optimisticEnrollment: Partial<CourseEnrollment> = {
      courseId,
      status: "enrolled",
      progressPercentage: 0,
      lastAccessedModuleId: null,
      enrolledAt: new Date().toISOString(),
      completedAt: null,
      cancelledAt: null,
    };
    dispatch(updateEnrollmentOptimistic(optimisticEnrollment));

    try {
      const response = await enrollCourse({ id: courseId });

      // Replace optimistic data with server response
      dispatch(setEnrollment(response.enrollment));
    } catch (error) {
      // Rollback optimistic update on error
      dispatch(setEnrollment(null));

      const message =
        error instanceof Error ? error.message : "Failed to enroll in course.";
      dispatch(setError(message));
    } finally {
      dispatch(setEnrollmentInProgress(false));
    }
  };
